import Typography from "@mui/material/Typography";
import Chip, { type ChipOwnProps } from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import { aiFillGradient, aiGlow } from "@/lib/scoring/components/AiBadge";
import {
  GridColDef,
  GridColumnGroup,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { abbreviateNumber, formatNumber } from "@/utils/numberUtils";
import { formatRevenueRange } from "./utils";
import { DataRow } from "./types";
import { CompanyNameCell } from "./components/CompanyNameCell";
import { CompanyTypeCell } from "./components/CompanyTypeCell";
import { ScoreChip, getChipColor } from "@/lib/scoring/components/ScoreChip";
import { getProfileScores, getModelScores } from "@/lib/scoring/scoringUtils";
import {
  type ScoreTone,
  TONE_HEX,
  compositeTone,
  tierTone,
} from "@/lib/scoring/scoreTone";
import { type ScoringProfile } from "@/lib/scoring/types";
import type { ScoringModel } from "@/apis/argo/scoringModels";
import {
  FIT_CRITERION_KEYS,
  INTENT_CRITERION_KEYS,
} from "@/lib/scoring/criterionRegistry";
import {
  MODEL_SCORE_PILLAR_LABELS,
  MODEL_SCORE_PILLARS,
  modelScoreField,
} from "@/lib/scoring/modelScoreFields";

const SCORE_CHIP_COLORS: ChipOwnProps["color"][] = [
  "success",
  "primary",
  "purple",
];

export const getBaseColumns = (): GridColDef<DataRow>[] => [
  {
    field: "name",
    headerName: "Company Name",
    minWidth: 200,
    flex: 1,
    sortable: true,
    renderCell: CompanyNameCell,
  },
  {
    field: "url",
    headerName: "URL",
    width: 100,
    sortable: true,
    renderCell: (params) => {
      return (
        <Tooltip title={params.row.url || ""} placement="bottom">
          <Typography variant="body2" noWrap>
            {params.row.url || ""}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "hierarchy",
    headerName: "Company Type",
    sortable: false,
    width: 200,
    renderCell: CompanyTypeCell,
  },
  {
    field: "country_name",
    headerName: "Country",
    sortable: true,
    minWidth: 200,
    flex: 1,
    renderCell: (params) => {
      return (
        <Tooltip title={params.row.countryName || ""} placement="bottom">
          <Typography variant="body2" noWrap>
            {params.row.countryName || ""}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "employee_range_min",
    headerName: "Employees",
    width: 130,
    sortable: true,
    renderCell: (params) => {
      const tooltipValue = params.row.employeeRange?.max
        ? formatNumber(params.row.employeeRange.max)
        : "";
      return params.row.employeeRange?.max ? (
        <Tooltip title={tooltipValue} placement="bottom">
          <Chip
            label={abbreviateNumber(params.row.employeeRange.max)}
            size="medium"
            sx={{
              bgcolor: "grey.200",
              width: 64,
            }}
          />
        </Tooltip>
      ) : null;
    },
  },
  {
    field: "revenue_range_min",
    headerName: "Revenue",
    width: 150,
    sortable: true,
    renderCell: (params) => {
      const displayValue = formatRevenueRange({
        min: params.row.revenueRange?.min ?? undefined,
        max: params.row.revenueRange?.max ?? undefined,
      });
      const tooltipValue = params.row.revenueRange?.max
        ? formatNumber(params.row.revenueRange.max)
        : displayValue;
      return (
        <Tooltip title={tooltipValue} placement="bottom">
          <Typography variant="body2">{displayValue}</Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "it_spend",
    headerName: "IT Spend",
    width: 130,
    sortable: true,
    renderCell: (params) => {
      const displayValue = params.row.itSpend
        ? abbreviateNumber(params.row.itSpend)
        : "";
      const tooltipValue = params.row.itSpend
        ? formatNumber(params.row.itSpend)
        : "";
      return (
        <Tooltip title={tooltipValue} placement="bottom">
          <Typography variant="body2">{displayValue}</Typography>
        </Tooltip>
      );
    },
  },
];

export interface ScoringColumnResult {
  columns: GridColDef<DataRow>[];
  columnGroupingModel: GridColumnGroup[];
}

export type OnScoreClick = (
  company: DataRow,
  profile: ScoringProfile,
  profileOrder: number,
  scoreType: "fit" | "intent"
) => void;

/** Fired when a V2 (argo model) score cell is clicked — opens the breakdown
 * drawer, which fetches the rule-level `explain` for this company + model. */
export type OnModelScoreClick = (company: DataRow, model: ScoringModel) => void;

export function getScoringColumns(
  activeProfiles: ScoringProfile[],
  currentSortField = "",
  onScoreClick?: OnScoreClick
): ScoringColumnResult {
  const columns: GridColDef<DataRow>[] = [];
  const columnGroupingModel: GridColumnGroup[] = [];

  activeProfiles.forEach((profile, profileIdx) => {
    const color = SCORE_CHIP_COLORS[profileIdx] ?? "default";

    const hasFit = profile.parameters.criteria.some(
      (c) =>
        typeof c === "object" &&
        c !== null &&
        FIT_CRITERION_KEYS.some((k) => k in (c as object))
    );
    const hasIntent = profile.parameters.criteria.some(
      (c) =>
        typeof c === "object" &&
        c !== null &&
        INTENT_CRITERION_KEYS.some((k) => k in (c as object))
    );

    const groupChildren: { field: string }[] = [];
    const profilePrefix = `score_${profile.id}_`;
    const isProfileSorted = currentSortField.startsWith(profilePrefix);

    const colWidth = hasFit && hasIntent ? 75 : 150;

    const makeColumn = (
      label: "Fit" | "Intent",
      scoreKey: "fitScoreNormalized" | "intentScoreNormalized"
    ): GridColDef<DataRow> => {
      const field = `score_${profile.id}_${label.toLowerCase()}`;
      groupChildren.push({ field });
      return {
        field,
        headerName: label,
        width: colWidth,
        sortable: true,
        sortingOrder: ["desc", "asc", null],
        align: "center",
        headerAlign: "center",
        cellClassName: isProfileSorted ? "score-col-sorted" : "",
        headerClassName: isProfileSorted ? "score-col-sorted" : "",
        renderCell: (params: GridRenderCellParams<DataRow>) => {
          const scores = getProfileScores(params.row.scores, profile.id);
          const chipColor = getChipColor(scores[scoreKey], profileIdx + 1);
          const scoreType = label.toLowerCase() as "fit" | "intent";
          return (
            <Box
              data-score-cell="true"
              role={onScoreClick ? "button" : undefined}
              tabIndex={onScoreClick ? 0 : undefined}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onScoreClick?.(params.row, profile, profileIdx + 1, scoreType);
              }}
              onKeyDown={(e) => {
                if (onScoreClick && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  e.stopPropagation();
                  onScoreClick(params.row, profile, profileIdx + 1, scoreType);
                }
              }}
              sx={{ cursor: onScoreClick ? "pointer" : "default" }}
            >
              <ScoreChip score={scores[scoreKey]} color={chipColor} />
            </Box>
          );
        },
      };
    };

    if (hasFit) columns.push(makeColumn("Fit", "fitScoreNormalized"));
    if (hasIntent) columns.push(makeColumn("Intent", "intentScoreNormalized"));

    if (groupChildren.length > 0) {
      const groupSorted = groupChildren.some(
        (c) => c.field === currentSortField
      );
      columnGroupingModel.push({
        groupId: `scoring_${profile.id}`,
        headerClassName: groupSorted ? "score-group-sorted" : "",
        renderHeaderGroup: () => (
          <Tooltip title={profile.name} placement="top">
            <Chip
              label={profile.name}
              size="small"
              color={color}
              sx={{
                maxWidth: "100%",
                borderRadius: 999,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  fontWeight: 700,
                },
              }}
            />
          </Tooltip>
        ),
        children: groupChildren,
      });
    }
  });

  return { columns, columnGroupingModel };
}

/** One tinted half of the two-tone V2 score chip (rgip Pill-style: 12% bg tint,
 * full-tone text). */
function ScoreToneSegment({
  value,
  tone,
  divide,
}: {
  value: string;
  tone: ScoreTone;
  divide: boolean;
}) {
  const hex = TONE_HEX[tone];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1,
        minWidth: 26,
        bgcolor: alpha(hex, 0.12),
        color: hex,
        ...(divide && { borderLeft: "1px solid rgba(0,0,0,0.08)" }),
      }}
    >
      {value}
    </Box>
  );
}

/**
 * Builds the V2 (argo model) score columns — one column group per active model
 * (header: model name + AI badge) with a "Total" column (`composite - tier`,
 * e.g. "30 - A") followed by Fit / Need / Intent columns. A pillar the model
 * doesn't score is null on every row and renders blank. Per-row values are
 * read from `row.model_scores[<family>]`. `composite` is a weighted 0-100 score
 * (pillars normalized 0-100), rendered as a bare number without a % sign.
 *
 * Every column is server-sortable via `model_score_sort` (wired in
 * CompaniesExploreList): Total sorts by `composite`; `tier` isn't sortable.
 */
export function getModelScoringColumns(
  activeModels: ScoringModel[],
  currentSortField = "",
  onModelScoreClick?: OnModelScoreClick
): ScoringColumnResult {
  const columns: GridColDef<DataRow>[] = [];
  const columnGroupingModel: GridColumnGroup[] = [];

  activeModels.forEach((model, index) => {
    const sortedClass = (field: string) =>
      currentSortField === field ? "score-col-sorted" : "";
    const common = {
      sortable: true,
      sortingOrder: ["desc", "asc", null] as GridColDef["sortingOrder"],
      align: "center" as const,
      headerAlign: "center" as const,
    };

    const groupChildren: { field: string }[] = [];
    const totalField = modelScoreField(model.family, "composite");
    groupChildren.push({ field: totalField });
    columns.push({
      ...common,
      // Field is the composite key so sorting maps to model_score_sort.composite.
      field: totalField,
      headerName: "Total",
      width: 110,
      cellClassName: sortedClass(totalField),
      headerClassName: sortedClass(totalField),
      renderCell: (params: GridRenderCellParams<DataRow>) => (
        <TotalScoreCell
          row={params.row}
          model={model}
          onModelScoreClick={onModelScoreClick}
        />
      ),
    });

    MODEL_SCORE_PILLARS.forEach((pillar) => {
      const field = modelScoreField(model.family, pillar);
      groupChildren.push({ field });
      columns.push({
        ...common,
        field,
        headerName: MODEL_SCORE_PILLAR_LABELS[pillar],
        width: 75,
        cellClassName: sortedClass(field),
        headerClassName: sortedClass(field),
        renderCell: (params: GridRenderCellParams<DataRow>) => {
          const value = getModelScores(params.row.model_scores, model.family)?.[
            pillar
          ];
          if (value == null) return null;
          return (
            <Typography
              variant="body2"
              fontWeight={600}
              data-testid={`scoring-v2-${pillar}-${model.family}`}
            >
              {Math.round(value)}
            </Typography>
          );
        },
      });
    });

    columnGroupingModel.push({
      groupId: `mscoring_${model.family}`,
      headerClassName: groupChildren.some((c) => c.field === currentSortField)
        ? "score-group-sorted"
        : "",
      renderHeaderGroup: () => (
        <Tooltip title={`${model.name} (new scoring)`} placement="top">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minWidth: 0,
              px: 0.5,
            }}
            data-testid={`scoring-model-chip-${model.family}`}
          >
            <Chip
              label={model.name}
              size="small"
              sx={{
                // Distinct self-gradient fill + colored glow (no border).
                minWidth: 0,
                maxWidth: "100%",
                borderRadius: 999,
                background: aiFillGradient(index),
                color: "#fff",
                boxShadow: aiGlow(index),
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        </Tooltip>
      ),
      children: groupChildren,
    });
  });

  return { columns, columnGroupingModel };
}

/** The two-tone `composite | tier` chip; clicking opens the breakdown drawer. */
function TotalScoreCell({
  row,
  model,
  onModelScoreClick,
}: {
  row: DataRow;
  model: ScoringModel;
  onModelScoreClick?: OnModelScoreClick;
}) {
  const scores = getModelScores(row.model_scores, model.family);
  const hasComposite = scores?.composite != null;
  const hasTier = Boolean(scores?.tier);
  if (!hasComposite && !hasTier) return null;
  const clickable = Boolean(onModelScoreClick);
  // Color the whole chip by the TIER — it's the model's own grade and
  // respects that model's cutoffs, so the number and the letter never
  // disagree (an "A" at composite 40 stays green, not red). Fall back to
  // the absolute composite band only when a row has a composite but no tier.
  const tone = hasTier
    ? tierTone(scores!.tier!)
    : compositeTone(scores!.composite!);
  // One chip, split into two tinted halves (composite point score | tier
  // letter), both on that shared tone.
  return (
    <Box
      data-score-cell="true"
      data-testid={`scoring-v2-score-${model.family}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onModelScoreClick?.(row, model);
      }}
      onKeyDown={(e) => {
        if (clickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          e.stopPropagation();
          onModelScoreClick?.(row, model);
        }
      }}
      sx={{
        display: "inline-flex",
        alignItems: "stretch",
        height: 24,
        borderRadius: 999,
        overflow: "hidden",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        cursor: clickable ? "pointer" : "default",
      }}
    >
      {hasComposite && (
        <ScoreToneSegment
          value={String(Math.round(scores!.composite!))}
          tone={tone}
          divide={false}
        />
      )}
      {hasTier && (
        <ScoreToneSegment
          value={scores!.tier!}
          tone={tone}
          divide={hasComposite}
        />
      )}
    </Box>
  );
}
